import * as SecureStore from "expo-secure-store";
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { Platform } from "react-native";

import { setAccountInactiveHandler } from "@/lib/account-status";
import { getApiBase } from "@/lib/api-env";
import { setSentryUser } from "@/lib/sentry-init";
import { signupTokenFrom, withSignupToken } from "@/lib/signup-pass";
import { otpRequestBody } from "@/lib/staff-login";

// expo-secure-store doesn't work on web — fall back to localStorage
const storage = {
    getItemAsync: (key: string) =>
        Platform.OS === "web"
            ? Promise.resolve(localStorage.getItem(key))
            : SecureStore.getItemAsync(key),
    setItemAsync: (key: string, value: string) =>
        Platform.OS === "web"
            ? Promise.resolve(localStorage.setItem(key, value))
            : SecureStore.setItemAsync(key, value),
    deleteItemAsync: (key: string) =>
        Platform.OS === "web"
            ? Promise.resolve(localStorage.removeItem(key))
            : SecureStore.deleteItemAsync(key)
};

const KEYS = {
    accessToken: "auth-access-token",
    refreshToken: "auth-refresh-token",
    user: "auth-user"
} as const;

// ---- Types ----

export type AuthStatus = "active" | "pending_application" | "new_user" | null;

export type RnStatus = "active" | "pending_application" | "suspended" | "rejected" | "terminated";

export interface AuthUser {
    id: string;
    phone: string;
    name: string | null;
    firstName: string | null;
    lastName: string | null;
    role: string;
    rnId: number | null;
    rnStatus: RnStatus | null;
}

export type AccountEndedReason = "terminated" | "deactivated";

interface AuthContextValue {
    user: AuthUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    authStatus: AuthStatus;
    pendingPhone: string | null;
    /**
     * Set when the server reports this nurse no longer has access. Non-null
     * means the app shows the account-ended screen; the session has already
     * been wiped by then, so this only controls what she is told.
     */
    accountEndedReason: AccountEndedReason | null;
    /** Reports the account as ended and wipes the local session immediately. */
    reportAccountEnded: (reason: AccountEndedReason) => void;
    /** Dismisses the account-ended screen and returns to the sign-in flow. */
    dismissAccountEnded: () => void;
    updateStoredUserName: (name: string) => Promise<void>;
    requestOtp: (
        phone: string,
        adminPassword?: string,
        operatorEmail?: string
    ) => Promise<{ success: boolean; error?: string }>;
    verifyOtp: (code: string) => Promise<{ success: boolean; error?: string }>;
    completeProfile: (data: {
        phone: string;
        firstName: string;
        middleName?: string;
        lastName: string;
        address: {
            formatted: string;
            lat: number;
            lng: number;
            line1: string;
            city: string;
            state: string;
            zip: string;
        };
    }) => Promise<{ success: boolean; error?: string }>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ---- Token accessors (used by api.ts) ----

let _accessToken: string | null = null;
let _refreshToken: string | null = null;
let _onLogout: (() => void) | null = null;
// Lets a token refresh push the freshened user back into React state, so a
// nurse whose status changed server-side stops being gated without needing to
// restart the app.
let _onUserRefreshed: ((user: AuthUser) => void) | null = null;

// Shared across all callers (api.ts, ticket-api.ts, onboarding-api.ts, etc.):
// the backend rotates refresh tokens single-use, so two concurrent /auth/refresh
// calls would race — the loser sends a now-revoked token and the user is logged out.
let _refreshInflight: Promise<string | null> | null = null;

export function getAccessToken(): string | null {
    return _accessToken;
}

export function getRefreshToken(): string | null {
    return _refreshToken;
}

async function performRefresh(): Promise<string | null> {
    if (!_refreshToken) {
        return null;
    }
    try {
        const res = await fetch(`${getApiBase()}/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken: _refreshToken })
        });
        if (!res.ok) {
            // Only a 401 means the refresh token itself is invalid/expired/revoked
            // — that is the ONLY case where we end the session. Every other
            // non-OK status (5xx, 429, other 4xx) is transient (a backend blip,
            // rate limit, or outage), so we keep the nurse logged in and just
            // signal failure with `null`. The backend keeps a 30s grace window
            // on refresh-token rotation, so a retry after a lost response still
            // succeeds — mass-logging-out on a transient error would be wrong.
            if (res.status === 401) {
                _onLogout?.();
            }
            return null;
        }
        const data = await res.json();
        _accessToken = data.accessToken;
        _refreshToken = data.refreshToken;
        await storage.setItemAsync(KEYS.accessToken, data.accessToken);
        await storage.setItemAsync(KEYS.refreshToken, data.refreshToken);
        // Refresh the cached user too. It used to be written ONLY at login,
        // and refresh tokens live a year — so a nurse activated after her last
        // login carried `rnStatus: "pending_application"` on her device
        // indefinitely and was treated as an applicant (no Smart Picks, and
        // "Finish your application to book" on a visit she was entitled to
        // take). Older servers omit `user`; then we simply keep what we have.
        if (data.user) {
            // Merge, don't replace: the server sends the authoritative identity
            // fields, and anything the client added locally survives.
            const cachedJson = await storage.getItemAsync(KEYS.user).catch(() => null);
            const cached = cachedJson ? (JSON.parse(cachedJson) as AuthUser) : null;
            const merged = { ...(cached ?? {}), ...data.user } as AuthUser;
            await storage.setItemAsync(KEYS.user, JSON.stringify(merged));
            _onUserRefreshed?.(merged);
        }
        return data.accessToken;
    } catch {
        // Network error / timeout — transient. Do NOT end the session; the
        // refresh token is almost certainly still valid. Just signal failure.
        return null;
    }
}

export async function refreshAccessToken(): Promise<string | null> {
    if (_refreshInflight) {
        return _refreshInflight;
    }
    _refreshInflight = performRefresh();
    try {
        return await _refreshInflight;
    } finally {
        _refreshInflight = null;
    }
}

export function triggerLogout(): void {
    _onLogout?.();
}

// ---- Provider ----

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [authStatus, setAuthStatus] = useState<AuthStatus>(null);
    const [pendingPhone, setPendingPhone] = useState<string | null>(null);
    const [accountEndedReason, setAccountEndedReason] = useState<AccountEndedReason | null>(null);
    const logoutRef = useRef<(() => Promise<void>) | undefined>(undefined);
    // The sign-up pass from otp/verify ("new_user"), sent back with
    // complete-profile so only this app can finish the registration of the
    // phone it just verified. Memory only, like pendingPhone: it lives 15
    // minutes and a restarted app verifies again anyway.
    const signupTokenRef = useRef<string | null>(null);

    const clearAuth = useCallback(async () => {
        _accessToken = null;
        _refreshToken = null;
        signupTokenRef.current = null;
        setUser(null);
        setAuthStatus(null);
        setPendingPhone(null);
        setSentryUser(null);
        await storage.deleteItemAsync(KEYS.accessToken).catch(() => {});
        await storage.deleteItemAsync(KEYS.refreshToken).catch(() => {});
        await storage.deleteItemAsync(KEYS.user).catch(() => {});
    }, []);

    /**
     * The account lost access. Wipe the session NOW — the point of this path is
     * that cached visits, chats, and patient details stop being readable — and
     * remember why so the UI can explain it instead of silently bouncing her to
     * the login screen.
     */
    const reportAccountEnded = useCallback(
        (reason: AccountEndedReason) => {
            setAccountEndedReason(reason);
            void clearAuth();
        },
        [clearAuth]
    );

    const dismissAccountEnded = useCallback(() => {
        setAccountEndedReason(null);
    }, []);

    // Register logout callback for token refresh flow
    useEffect(() => {
        logoutRef.current = clearAuth;
        _onLogout = () => {
            logoutRef.current?.();
        };
        _onUserRefreshed = (refreshed: AuthUser) => {
            setUser(refreshed);
        };
        return () => {
            _onLogout = null;
            _onUserRefreshed = null;
        };
    }, [clearAuth]);

    // Any 403 ACCOUNT_INACTIVE from any endpoint routes here.
    useEffect(() => {
        setAccountInactiveHandler(reportAccountEnded);
        return () => setAccountInactiveHandler(null);
    }, [reportAccountEnded]);

    // Restore session on mount
    useEffect(() => {
        (async () => {
            try {
                const [token, refresh, userJson] = await Promise.all([
                    storage.getItemAsync(KEYS.accessToken),
                    storage.getItemAsync(KEYS.refreshToken),
                    storage.getItemAsync(KEYS.user)
                ]);
                if (token && refresh && userJson) {
                    _accessToken = token;
                    _refreshToken = refresh;
                    setUser(JSON.parse(userJson));
                }
            } catch {
                // Corrupted store — clear it
                await clearAuth();
            } finally {
                setIsLoading(false);
            }
        })();
    }, [clearAuth]);

    const requestOtp = useCallback(
        async (
            phone: string,
            adminPassword?: string,
            operatorEmail?: string
        ): Promise<{ success: boolean; error?: string }> => {
            try {
                // adminPassword is the hidden staff login. Only included when the
                // tester revealed the field and typed something — normal users
                // never send it. A correct value makes the backend skip the SMS
                // and accept the fixed code 456789 (see otp.service.ts).
                // operatorEmail (her own staff email) goes with it, so the
                // server records who used the password (lib/staff-login.ts).
                const res = await fetch(`${getApiBase()}/auth/otp/request`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(otpRequestBody(phone, adminPassword, operatorEmail))
                });
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    const msg =
                        typeof body.error === "string"
                            ? body.error
                            : body.error?.message || `Request failed: ${res.status}`;
                    return { success: false, error: msg };
                }
                setPendingPhone(phone);
                return { success: true };
            } catch (err: any) {
                return { success: false, error: err.message || "Network error" };
            }
        },
        []
    );

    const verifyOtp = useCallback(
        async (code: string): Promise<{ success: boolean; error?: string }> => {
            if (!pendingPhone) {
                return { success: false, error: "No pending phone number" };
            }
            try {
                const res = await fetch(`${getApiBase()}/auth/otp/verify`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ identifier: pendingPhone, code })
                });
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    const msg =
                        typeof body.error === "string"
                            ? body.error
                            : body.error?.message || "Verification failed";
                    return { success: false, error: msg };
                }
                const data = await res.json();

                if (data.status === "new_user") {
                    // No tokens: mark as new user and keep the sign-up pass
                    // (absent from an older API; complete-profile then goes
                    // without it, as before).
                    signupTokenRef.current = signupTokenFrom(data);
                    setAuthStatus("new_user");
                    return { success: true };
                }

                // Active or pending_application — tokens present
                _accessToken = data.accessToken;
                _refreshToken = data.refreshToken;
                const authUser: AuthUser = data.user;

                await Promise.all([
                    storage.setItemAsync(KEYS.accessToken, data.accessToken),
                    storage.setItemAsync(KEYS.refreshToken, data.refreshToken),
                    storage.setItemAsync(KEYS.user, JSON.stringify(authUser))
                ]);

                setUser(authUser);
                setAuthStatus(data.status);
                return { success: true };
            } catch (err: any) {
                return { success: false, error: err.message || "Network error" };
            }
        },
        [pendingPhone]
    );

    const completeProfile = useCallback(
        async (data: {
            phone: string;
            firstName: string;
            middleName?: string;
            lastName: string;
            address: {
                formatted: string;
                lat: number;
                lng: number;
                line1: string;
                city: string;
                state: string;
                zip: string;
            };
        }): Promise<{ success: boolean; error?: string }> => {
            try {
                const res = await fetch(`${getApiBase()}/auth/complete-profile`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(withSignupToken(data, signupTokenRef.current))
                });

                const body = await res.json();

                if (!res.ok) {
                    const msg =
                        typeof body.error === "string"
                            ? body.error
                            : body.error?.message || "Profile creation failed";
                    return { success: false, error: msg };
                }

                // Store tokens + user. The pass did its one job.
                signupTokenRef.current = null;
                _accessToken = body.accessToken;
                _refreshToken = body.refreshToken;
                const authUser: AuthUser = body.user;

                await Promise.all([
                    storage.setItemAsync(KEYS.accessToken, body.accessToken),
                    storage.setItemAsync(KEYS.refreshToken, body.refreshToken),
                    storage.setItemAsync(KEYS.user, JSON.stringify(authUser))
                ]);

                setUser(authUser);
                setAuthStatus("pending_application");
                return { success: true };
            } catch (err: any) {
                return { success: false, error: err.message || "Network error" };
            }
        },
        []
    );

    const logout = useCallback(async () => {
        // Try to notify server, but don't block on it
        if (_refreshToken) {
            fetch(`${getApiBase()}/auth/logout`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refreshToken: _refreshToken })
            }).catch(() => {});
        }
        await clearAuth();
    }, [clearAuth]);

    const updateStoredUserName = useCallback(
        async (name: string) => {
            if (user) {
                const updated = { ...user, name };
                setUser(updated);
                await storage.setItemAsync(KEYS.user, JSON.stringify(updated));
            }
        },
        [user]
    );

    const value: AuthContextValue = {
        user,
        isAuthenticated: user !== null,
        isLoading,
        authStatus,
        pendingPhone,
        accountEndedReason,
        reportAccountEnded,
        dismissAccountEnded,
        updateStoredUserName,
        requestOtp,
        verifyOtp,
        completeProfile,
        logout
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
}
